import { useMemo, useState } from 'react';

import type { LocalPick } from '../../lib/localPicks.js';

import { Button } from '../../components/Button.js';
import { useI18n } from '../../i18n/index.js';
import { categoryName as nameOf, type LoadedCatalog } from '../../lib/catalog.js';
import { resolveMergePlan, type ConflictChoice, type MergePlan } from './mergePlan.js';
import styles from './MergeSheet.module.css';

interface MergeSheetProps {
  plan: MergePlan;
  catalog: LoadedCatalog | null;
  submitting: boolean;
  onApply: (picks: LocalPick[]) => void;
  /** 계정 것을 그대로 두고 로컬은 버린다 */
  onSkip: () => void;
}

/**
 * 병합 충돌 해결.
 *
 * **겹치면서 내용이 다른 것만 보여준다.** 전부 나열하면 무엇이 문제인지
 * 묻혀서, 사용자는 대충 넘기게 되고 그게 곧 데이터 손실이다.
 *
 * 기본 선택은 **계정에 저장된 것**이다 — 아무것도 고르지 않고 넘겨도
 * 이미 저장된 데이터는 지켜진다.
 */
export function MergeSheet({ plan, catalog, submitting, onApply, onSkip }: MergeSheetProps) {
  const { t } = useI18n();
  const [choices, setChoices] = useState<Map<string, ConflictChoice>>(new Map());

  const resolved = useMemo(() => resolveMergePlan(plan, choices), [plan, choices]);

  const label = (categoryId: string) =>
    catalog === null ? categoryId : nameOf(catalog, categoryId);

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)', marginBottom: 'var(--s4)' }}>
        {t('merge.lead')}
      </p>

      {plan.toAdd.length > 0 ? (
        <p className={styles.summary}>{t('merge.autoAdded', { count: plan.toAdd.length })}</p>
      ) : null}

      {plan.conflicts.length > 0 ? (
        <>
          <h3 className={styles.conflictTitle}>{t('merge.conflictTitle')}</h3>
          <p className={styles.conflictLead}>
            {t('merge.conflictLead', { count: plan.conflicts.length })}
          </p>

          {plan.conflicts.map((conflict) => {
            const choice = choices.get(conflict.categoryId) ?? 'server';
            return (
              <div key={conflict.categoryId} className={styles.conflict}>
                <div className={styles.category}>{label(conflict.categoryId)}</div>
                <div className={styles.options}>
                  <button
                    type="button"
                    className={styles.option}
                    aria-pressed={choice === 'local'}
                    onClick={() =>
                      setChoices((current) => new Map(current).set(conflict.categoryId, 'local'))
                    }
                  >
                    <span className={styles.optionLabel}>{t('merge.keepLocal')}</span>
                    <span className={styles.optionName}>{conflict.local.itemName}</span>
                    {conflict.local.itemDescription !== undefined ? (
                      <span className={styles.optionDesc}>{conflict.local.itemDescription}</span>
                    ) : null}
                  </button>

                  <button
                    type="button"
                    className={styles.option}
                    aria-pressed={choice === 'server'}
                    onClick={() =>
                      setChoices((current) => new Map(current).set(conflict.categoryId, 'server'))
                    }
                  >
                    <span className={styles.optionLabel}>{t('merge.keepServer')}</span>
                    <span className={styles.optionName}>{conflict.server.itemName}</span>
                    {conflict.server.itemDescription !== undefined ? (
                      <span className={styles.optionDesc}>{conflict.server.itemDescription}</span>
                    ) : null}
                  </button>
                </div>
              </div>
            );
          })}
        </>
      ) : null}

      <div className={styles.footer}>
        <Button fullWidth loading={submitting} onClick={() => onApply(resolved)}>
          {t('merge.apply')}
        </Button>
        <Button variant="quiet" fullWidth disabled={submitting} onClick={onSkip}>
          {t('merge.skipAll')}
        </Button>
      </div>
    </div>
  );
}
